// @ts-nocheck
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

const GhostPluginKey = new PluginKey("ghostSuggestion");

export const GhostSuggestion = Extension.create({
  name: "ghostSuggestion",

  addCommands() {
    return {
      setGhostSuggestion:
        (text: string) =>
        ({ state, dispatch }) => {
          const { from, to } = state.selection;
          if (from !== to) return false;
          const tr = state.tr.setMeta(GhostPluginKey, { type: "set", text, pos: from });
          dispatch?.(tr);
          return true;
        },
      clearGhostSuggestion:
        () =>
        ({ state, dispatch }) => {
          const tr = state.tr.setMeta(GhostPluginKey, { type: "clear" });
          dispatch?.(tr);
          return true;
        },
      setGhostVariants:
        (variants: string[]) =>
        ({ state, dispatch }) => {
          const tr = state.tr.setMeta(GhostPluginKey, { type: "variants", variants });
          dispatch?.(tr);
          return true;
        },
      cycleGhostVariant:
        (dir: number) =>
        ({ state, dispatch }) => {
          const tr = state.tr.setMeta(GhostPluginKey, { type: "cycle", dir });
          dispatch?.(tr);
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const plugin = new Plugin<{ text: string; pos: number | null; decorations: DecorationSet; variants: string[]; index: number }>({
      key: GhostPluginKey,
      state: {
        init: (_, _state) => ({ text: "", pos: null, decorations: DecorationSet.empty, variants: [], index: 0 }),
        apply: (tr, prev, _oldState, newState) => {
          const meta = tr.getMeta(GhostPluginKey) as
            | { type: "set"; text: string; pos: number }
            | { type: "clear" }
            | { type: "variants"; variants: string[] }
            | { type: "cycle"; dir: number }
            | undefined;
          let text = prev.text;
          let pos = prev.pos;
          let decorations = prev.decorations.map(tr.mapping, tr.doc);
          let variants = prev.variants;
          let index = prev.index;

          if (meta?.type === "set") {
            text = meta.text;
            pos = meta.pos;
          } else if (meta?.type === "clear") {
            text = "";
            pos = null;
            index = 0;
            // keep current variants
          } else if (meta?.type === "variants") {
            variants = meta.variants ?? [];
            index = 0;
          } else if (meta?.type === "cycle") {
            if (variants.length > 0) {
              index = (index + (meta.dir > 0 ? 1 : -1) + variants.length) % variants.length;
              text = variants[index] ?? text;
            }
          }

          // Clear automatically when selection expands
          const sel = newState.selection;
          if (sel.from !== sel.to) {
            text = "";
            pos = null;
          }

          // Rebuild decoration
          if (!text || pos == null) {
            decorations = DecorationSet.empty;
          } else {
            const deco = Decoration.widget(
              Math.max(0, Math.min(pos, newState.doc.content.size)),
              () => {
                const span = document.createElement("span");
                span.className = "ghost-suggestion";
                span.style.opacity = "0.55";
                span.style.color = "var(--muted-foreground)";
                span.style.pointerEvents = "auto"; // allow hover
                span.style.whiteSpace = "pre-wrap";
                span.textContent = text;
                // Intercept wheel on the ghost element to avoid page scroll and cycle variants directly
                span.addEventListener(
                  "wheel",
                  (e) => {
                    const we = e as WheelEvent;
                    if (!we.shiftKey) return; // only cycle on Shift + Scroll
                    we.preventDefault();
                    we.stopPropagation();
                    const dir = we.deltaY > 0 ? 1 : -1;
                    try {
                      editor.commands.cycleGhostVariant(dir);
                    } catch {}
                  },
                  { passive: false }
                );
                return span;
              },
              { side: 1 }
            );
            decorations = DecorationSet.create(newState.doc, [deco]);
          }

          return { text, pos, decorations, variants, index };
        },
      },
      props: {
        decorations: (state) => plugin.getState(state)?.decorations ?? DecorationSet.empty,
      },
    });

    return [plugin];
  },
});

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    ghostSuggestion: {
      setGhostSuggestion: (text: string) => ReturnType;
      clearGhostSuggestion: () => ReturnType;
      setGhostVariants: (variants: string[]) => ReturnType;
      cycleGhostVariant: (dir: number) => ReturnType;
    };
  }
}
